import { z } from 'zod';

import { MediaTypes } from 'src/app/pages/startAPetitionV2/pageContext';

// the only required fields for publishing are `ask` and `description`
const askSchema = z.string().min(1);
const descriptionSchema = z.string().min(1);

const geoReachSchema = z.enum(['global', 'national', 'local']);
const locationSchema = z.object({ name: z.string().min(1), id: z.string().min(1) });
const imageSchema = z.object({ url: z.string().min(1), type: z.enum(Object.values(MediaTypes)) });

const recommendedDraftSchema = z.object({
	ask: askSchema.nullable().optional(),
	description: descriptionSchema.nullable().optional(),
	image: z
		.object({ url: z.string().min(1) })
		.nullable()
		.optional(),
});
const petitionDraftSchema = z.object({
	ask: askSchema,
	description: descriptionSchema,
	image: imageSchema.nullable().optional(),
	reach: geoReachSchema.nullable().optional(),
	location: locationSchema.nullable().optional(),
});
const inputSchema = z.object({
	recommendedDraft: recommendedDraftSchema.nullable().optional(), // don't block publishing if AI draft is missing
	petitionDraft: petitionDraftSchema,
});

// the output schema validates the input transformations
// and ensures the result matches the schema passed to the mutation
const outputSchema = z.object({
	ask: askSchema,
	description: descriptionSchema,
	aiAsk: z.string().nullable().optional(),
	aiDescription: z.string().nullable().optional(),
	geoReach: geoReachSchema.nullable().optional(),
	location: z.object({ localizedName: z.string(), googlePlacesId: z.string() }).nullable().optional(),
	media: z
		.object({ photo: z.object({ srcUrl: z.string().min(1), type: z.enum(Object.values(MediaTypes)).optional() }) })
		.nullable()
		.optional(),
});

const transformLocation = (location: z.infer<typeof locationSchema> | null | undefined) => {
	return location
		? {
				localizedName: location.name,
				googlePlacesId: location.id,
			}
		: undefined;
};

const transformImage = (image: z.infer<typeof petitionDraftSchema.shape.image>) => {
	return image
		? {
				photo: {
					srcUrl: image.url,
					type: image.type,
				},
			}
		: undefined;
};
const transformBaseSchema = (input: z.infer<typeof inputSchema>): z.infer<typeof outputSchema> => {
	const { recommendedDraft, petitionDraft } = input;
	return {
		ask: petitionDraft.ask,
		description: petitionDraft.description,
		aiAsk: recommendedDraft?.ask,
		aiDescription: recommendedDraft?.description,
		geoReach: petitionDraft.reach,
		location: transformLocation(petitionDraft.location),
		media: transformImage(petitionDraft.image),
	};
};

export const schema = inputSchema.transform(transformBaseSchema).pipe(outputSchema);
export type Schema = z.infer<typeof schema>;
